// ---------------------------------------------------------------------------
// PB-T.16: 20 Section Templates for "+ Add Section" visual picker
// ---------------------------------------------------------------------------
// Each template produces valid Puck JSON with realistic default content.
// Grouped by category for the visual picker (section-picker.tsx).
// ---------------------------------------------------------------------------

export interface SectionTemplate {
  id: string;
  name: string;
  category:
    | "hero"
    | "features"
    | "testimonials"
    | "pricing"
    | "cta"
    | "faq"
    | "other";
  thumbnail: string;
  content: Array<{ type: string; props: Record<string, unknown> }>;
}

// ---------------------------------------------------------------------------
// Category metadata for the picker UI
// ---------------------------------------------------------------------------
export const TEMPLATE_CATEGORIES = [
  { id: "hero", label: "Heroes", icon: "Sparkles" },
  { id: "features", label: "Features", icon: "Grid3x3" },
  { id: "testimonials", label: "Testimonials", icon: "Quote" },
  { id: "pricing", label: "Pricing", icon: "DollarSign" },
  { id: "cta", label: "Call to Action", icon: "MousePointerClick" },
  { id: "faq", label: "FAQ", icon: "HelpCircle" },
  { id: "other", label: "Other", icon: "LayoutGrid" },
] as const;

// ---------------------------------------------------------------------------
// 20 Section Templates
// ---------------------------------------------------------------------------
export const SECTION_TEMPLATES: SectionTemplate[] = [
  // ---- Heroes (3) ----
  {
    id: "hero-image-bg",
    name: "Image Background Hero",
    category: "hero",
    thumbnail:
      "linear-gradient(135deg, var(--color-primary) 0%, #0a2540 100%)",
    content: [
      {
        type: "Hero",
        props: {
          title: "Grow Your Business With Confidence",
          subtitle:
            "The all-in-one platform trusted by 10,000+ service businesses to book, manage, and retain clients",
          ctaLabel: "Start Free Trial",
          ctaUrl: "/signup",
          align: "center",
          backgroundImage: "/images/hero-bg.jpg",
          overlay: true,
        },
      },
    ],
  },
  {
    id: "hero-gradient",
    name: "Gradient Hero",
    category: "hero",
    thumbnail:
      "linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)",
    content: [
      {
        type: "Hero",
        props: {
          title: "Turn First-Time Visitors Into Lifetime Clients",
          subtitle:
            "Automated follow-ups, smart scheduling, and AI-powered insights that keep your calendar full",
          ctaLabel: "Get Started Free",
          ctaUrl: "/signup",
          secondaryCtaLabel: "Watch Demo",
          secondaryCtaUrl: "/demo",
          align: "center",
          background:
            "linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)",
        },
      },
    ],
  },
  {
    id: "hero-split",
    name: "Split Hero",
    category: "hero",
    thumbnail:
      "linear-gradient(90deg, var(--color-card) 50%, var(--color-muted) 50%)",
    content: [
      {
        type: "Columns",
        props: {
          columns: 2,
          gap: "48px",
          verticalAlign: "center",
        },
      },
      {
        type: "Heading",
        props: {
          text: "Everything Your Business Needs in One Place",
          level: "h1",
        },
      },
      {
        type: "Text",
        props: {
          content:
            "CRM, scheduling, email marketing, AI assistant, and a website builder — no more switching between 5 different tools.",
        },
      },
      {
        type: "Button",
        props: {
          label: "Book a Free Demo",
          url: "/book",
          variant: "primary",
        },
      },
      {
        type: "Image",
        props: {
          src: "/images/dashboard-preview.jpg",
          alt: "Platform dashboard showing client pipeline and upcoming appointments",
        },
      },
    ],
  },

  // ---- Features (3) ----
  {
    id: "features-icon-grid",
    name: "Icon Feature Grid",
    category: "features",
    thumbnail:
      "linear-gradient(180deg, var(--color-background) 0%, var(--color-muted) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Built for Service Businesses",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "FeatureGrid",
        props: {
          columns: "3",
          features:
            "Online Booking | Let clients book 24/7 from your website, Google, or social media\nSmart CRM | Track every interaction and never let a lead slip through the cracks\nAI Follow-Ups | Automated messages that sound personal and arrive at the perfect moment",
        },
      },
    ],
  },
  {
    id: "features-alternating",
    name: "Alternating Features",
    category: "features",
    thumbnail:
      "linear-gradient(180deg, var(--color-card) 0%, var(--color-background) 100%)",
    content: [
      {
        type: "Columns",
        props: { columns: 2, gap: "32px", verticalAlign: "center" },
      },
      {
        type: "Image",
        props: {
          src: "/images/analytics-preview.jpg",
          alt: "Revenue analytics dashboard with growth chart",
        },
      },
      {
        type: "Text",
        props: {
          content:
            "See which marketing channels drive the most revenue. Track every client from first click to paid invoice with zero manual work.",
        },
      },
      {
        type: "Text",
        props: {
          content:
            "Your AI assistant monitors client behavior and flags at-risk clients before they churn. Act on real data, not gut feelings.",
        },
      },
      {
        type: "Image",
        props: {
          src: "/images/ai-insights-preview.jpg",
          alt: "AI-powered client health scores and churn prediction",
        },
      },
    ],
  },
  {
    id: "features-card-grid",
    name: "Feature Card Grid",
    category: "features",
    thumbnail:
      "linear-gradient(135deg, var(--color-card) 0%, var(--color-primary-light, #e0e7ff) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Why Teams Switch to Us",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "FeatureGrid",
        props: {
          columns: "3",
          features:
            "Revenue Dashboard | Know exactly how much each service, staff member, and campaign earns\nReputation Manager | Collect 5-star reviews on autopilot after every appointment\nTeam Performance | See who converts the most leads and who needs coaching\nAutomated Workflows | Set it once, let the system handle reminders, follow-ups, and re-engagement\nClient Portal | Clients book, reschedule, and pay without calling your front desk\nMobile App | Manage your business from anywhere with real-time notifications",
        },
      },
    ],
  },

  // ---- Testimonials (2) ----
  {
    id: "testimonials-carousel",
    name: "Testimonial Carousel",
    category: "testimonials",
    thumbnail:
      "linear-gradient(135deg, var(--color-accent) 0%, var(--color-primary) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Trusted by Businesses Like Yours",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "Testimonials",
        props: {
          layout: "carousel",
          quotes:
            "We went from scattered spreadsheets to a fully automated client pipeline. Revenue is up 42% this quarter. | Sarah Mitchell | Owner, Zen Yoga Studio\nThe AI follow-ups recovered 23 lost clients in the first month. That alone paid for the platform. | Dr. James Park | Bright Dental\nMy team saves 15 hours a week on admin. We can finally focus on what matters: our clients. | Emily Torres | Director, Fresh Salon Group",
        },
      },
    ],
  },
  {
    id: "testimonials-grid",
    name: "Testimonial Grid",
    category: "testimonials",
    thumbnail:
      "linear-gradient(180deg, var(--color-muted) 0%, var(--color-card) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "What Our Clients Say",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "Testimonials",
        props: {
          layout: "grid",
          quotes:
            "Best investment we made this year. The booking system alone saves us 10 hours a week. | Maria Gonzalez | Peak Performance Coaching\nFinally, software that understands service businesses. Not another generic CRM. | Liam O'Brien | Greenfield Landscaping\nThe reputation management tool got us from 3.8 to 4.7 stars in two months. | Ana Petrova | Bloom Day Spa\nOur no-show rate dropped from 18% to 3% after enabling smart reminders. | David Kim | Seoul Kitchen",
        },
      },
    ],
  },

  // ---- Pricing (2) ----
  {
    id: "pricing-three-tier",
    name: "3-Tier Pricing",
    category: "pricing",
    thumbnail:
      "linear-gradient(180deg, var(--color-background) 0%, var(--color-primary-light, #e0e7ff) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Simple, Transparent Pricing",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "PricingTable",
        props: {
          tier1Name: "Starter",
          tier1Price: "$29/mo",
          tier1Features:
            "1 location\n500 contacts\nOnline booking\nEmail support\nBasic analytics",
          tier2Name: "Growth",
          tier2Price: "$89/mo",
          tier2Features:
            "5 locations\n5,000 contacts\nAI follow-ups\nReputation management\nPriority support\nAdvanced analytics",
          tier3Name: "Scale",
          tier3Price: "$199/mo",
          tier3Features:
            "Unlimited locations\nUnlimited contacts\nAI assistant\nCustom integrations\nDedicated success manager\nAPI access",
        },
      },
    ],
  },
  {
    id: "pricing-comparison",
    name: "Feature Comparison",
    category: "pricing",
    thumbnail:
      "linear-gradient(135deg, var(--color-card) 0%, var(--color-muted) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Compare Plans",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "PricingTable",
        props: {
          tier1Name: "Free",
          tier1Price: "$0/mo",
          tier1Features:
            "1 location\n500 contacts\n3 automations\nCommunity support",
          tier2Name: "Pro",
          tier2Price: "$89/mo",
          tier2Features:
            "10 locations\n25,000 contacts\nUnlimited automations\nAI features\nPriority support\nCustom branding",
          tier3Name: "Enterprise",
          tier3Price: "Custom",
          tier3Features:
            "Unlimited everything\nDedicated infrastructure\nSSO & SAML\nSLA guarantee\nCustom integrations\nWhite-label option",
        },
      },
    ],
  },

  // ---- CTA (2) ----
  {
    id: "cta-centered",
    name: "Centered CTA",
    category: "cta",
    thumbnail:
      "linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)",
    content: [
      {
        type: "Hero",
        props: {
          title: "Ready to Grow Your Business?",
          subtitle:
            "Join 10,000+ service businesses that increased revenue by an average of 35% in their first 90 days",
          ctaLabel: "Start Your Free Trial",
          ctaUrl: "/signup",
          align: "center",
          background: "var(--color-primary)",
          textColor: "white",
        },
      },
    ],
  },
  {
    id: "cta-split-image",
    name: "Split CTA with Image",
    category: "cta",
    thumbnail:
      "linear-gradient(90deg, var(--color-primary) 50%, var(--color-muted) 50%)",
    content: [
      {
        type: "Columns",
        props: { columns: 2, gap: "32px", verticalAlign: "center" },
      },
      {
        type: "Heading",
        props: {
          text: "Still Have Questions?",
          level: "h2",
        },
      },
      {
        type: "Text",
        props: {
          content:
            "Our team will walk you through the platform, answer your questions, and help you build a custom growth plan. No pressure, no commitment.",
        },
      },
      {
        type: "Button",
        props: {
          label: "Book a Free 15-Minute Call",
          url: "/book",
          variant: "primary",
        },
      },
      {
        type: "Image",
        props: {
          src: "/images/support-team.jpg",
          alt: "Friendly support team member on a video call with a client",
        },
      },
    ],
  },

  // ---- FAQ (2) ----
  {
    id: "faq-accordion",
    name: "FAQ Accordion",
    category: "faq",
    thumbnail:
      "linear-gradient(180deg, var(--color-background) 0%, var(--color-muted) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Frequently Asked Questions",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "FaqAccordion",
        props: {
          items:
            "How long is the free trial? | 14 days with full access to every feature. No credit card required to start.\nCan I cancel anytime? | Yes. No contracts, no cancellation fees. You keep your data if you decide to leave.\nDo I need technical skills? | Not at all. The drag-and-drop interface requires zero coding. Most businesses are live in under 10 minutes.\nIs my data secure? | Absolutely. We use bank-level encryption, are GDPR compliant, and your data is always yours to export.\nDo you offer custom plans? | Yes. Contact our sales team for enterprise pricing tailored to your team size and needs.\nCan I migrate from another platform? | We offer free migration from any major platform including GHL, HubSpot, and Mindbody.",
        },
      },
    ],
  },
  {
    id: "faq-two-column",
    name: "Two-Column FAQ",
    category: "faq",
    thumbnail:
      "linear-gradient(90deg, var(--color-card) 50%, var(--color-card) 50%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Common Questions",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "FaqAccordion",
        props: {
          layout: "columns",
          items:
            "What is included? | CRM, email marketing, booking, website builder, AI assistant, and more. All in one subscription.\nAny setup fees? | None. Start in minutes with our guided onboarding wizard.\nPhone support? | Available on Growth and Scale plans. All plans include email and chat support.\nData migration? | Free migration from any platform. Our team handles the heavy lifting.\nAPI access? | Available on Scale and Enterprise plans. Full REST API with TypeScript SDK.\nWhite-label option? | Enterprise customers can fully rebrand the platform for their own clients.",
        },
      },
    ],
  },

  // ---- Other (6) ----
  {
    id: "contact-form",
    name: "Contact Form",
    category: "other",
    thumbnail:
      "linear-gradient(135deg, var(--color-card) 0%, var(--color-muted) 100%)",
    content: [
      {
        type: "Columns",
        props: { columns: 2, gap: "32px", verticalAlign: "top" },
      },
      {
        type: "ContactForm",
        props: {
          heading: "Get in Touch",
          submitLabel: "Send Message",
          successMessage:
            "Thanks for reaching out. We will get back to you within 24 hours.",
        },
      },
      {
        type: "Text",
        props: {
          content:
            "123 Business Avenue, Suite 200\nNew York, NY 10001\n\nPhone: (555) 123-4567\nEmail: hello@yourbusiness.com\n\nMon-Fri: 9am - 6pm EST",
        },
      },
    ],
  },
  {
    id: "gallery-masonry",
    name: "Image Gallery",
    category: "other",
    thumbnail:
      "linear-gradient(135deg, var(--color-muted) 0%, var(--color-card) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Our Work",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "Gallery",
        props: {
          columns: "3",
          images: "",
        },
      },
    ],
  },
  {
    id: "team-grid",
    name: "Team Members",
    category: "other",
    thumbnail:
      "linear-gradient(180deg, var(--color-background) 0%, var(--color-card) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Meet the Team",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "FeatureGrid",
        props: {
          columns: "4",
          features:
            "Dr. Sarah Mitchell | Founder and Lead Practitioner with 15 years of experience\nJames Park | Operations Director keeping everything running smoothly\nEmily Chen | Head of Client Success ensuring every client is happy\nMichael Torres | Technical Lead building the technology behind the scenes",
        },
      },
    ],
  },
  {
    id: "stats-bar",
    name: "Stats Counter Bar",
    category: "other",
    thumbnail:
      "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
    content: [
      {
        type: "StatsCounter",
        props: {
          stats: "10,000+ | Active Businesses\n98% | Client Satisfaction\n24/7 | Platform Uptime\n35% | Average Revenue Increase",
        },
      },
    ],
  },
  {
    id: "booking-inline",
    name: "Inline Booking Widget",
    category: "other",
    thumbnail:
      "linear-gradient(135deg, var(--color-accent) 0%, var(--color-primary) 100%)",
    content: [
      {
        type: "Heading",
        props: {
          text: "Book Your Appointment",
          level: "h2",
          align: "center",
        },
      },
      {
        type: "Text",
        props: {
          content:
            "Pick a date and time that works for you. We will confirm your appointment instantly via email.",
        },
      },
      {
        type: "ContactForm",
        props: {
          heading: "Schedule Now",
          submitLabel: "Book Appointment",
          successMessage:
            "Your appointment is confirmed. Check your email for details.",
        },
      },
    ],
  },
  {
    id: "footer-full",
    name: "Full Footer",
    category: "other",
    thumbnail:
      "linear-gradient(180deg, var(--color-muted) 0%, #1a1a2e 100%)",
    content: [
      {
        type: "Columns",
        props: { columns: 4, gap: "24px" },
      },
      {
        type: "Text",
        props: {
          content:
            "Your Business Name\n\nThe all-in-one platform for service businesses. Book, manage, grow.",
        },
      },
      {
        type: "Text",
        props: {
          content:
            "Product\n\nFeatures\nPricing\nIntegrations\nAPI Documentation\nChangelog",
        },
      },
      {
        type: "Text",
        props: {
          content:
            "Company\n\nAbout Us\nCareers\nBlog\nPartner Program\nContact",
        },
      },
      {
        type: "Text",
        props: {
          content:
            "Legal\n\nPrivacy Policy\nTerms of Service\nGDPR Compliance\nCookie Policy\nSecurity",
        },
      },
    ],
  },
];
